import { supabase } from './supabase';

/**
 * 2FA (TOTP) via Supabase Auth MFA.
 * RÉTRO-COMPATIBLE : un compte SANS facteur reste en aal1 → aucune étape ajoutée
 * (connexion identique à avant). Un compte AVEC TOTP vérifié doit saisir un code
 * après le mot de passe (élévation aal1 → aal2). Logique pure `needsStepUp` testée.
 */

export interface AalLevels {
  currentLevel: string | null;
  nextLevel: string | null;
}

/**
 * Faut-il un second facteur maintenant ?
 * Vrai si l'utilisateur a un TOTP vérifié (nextLevel aal2) mais la session est
 * encore au niveau mot de passe (currentLevel aal1).
 */
export function needsStepUp(aal: AalLevels): boolean {
  return aal.nextLevel === 'aal2' && aal.currentLevel === 'aal1';
}

export interface MfaFactor {
  id: string;
  friendlyName: string | null;
  status: string;
}

/** Niveaux d'assurance de la session courante. */
export async function getAal(): Promise<AalLevels> {
  const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  return { currentLevel: data?.currentLevel ?? null, nextLevel: data?.nextLevel ?? null };
}

/** Liste les facteurs TOTP de l'utilisateur connecté. */
export async function listTotpFactors(): Promise<MfaFactor[]> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error || !data) return [];
  const totp = (data as { totp?: { id: string; friendly_name?: string | null; status: string }[] }).totp ?? [];
  return totp.map((f) => ({ id: f.id, friendlyName: f.friendly_name ?? null, status: f.status }));
}

export interface EnrollResult {
  ok: boolean;
  factorId?: string;
  qrCode?: string;
  secret?: string;
  error?: string;
}

/** Démarre l'activation TOTP : renvoie le QR code (data-URI) + le secret à scanner. */
export async function enrollTotp(): Promise<EnrollResult> {
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: `YumLink-${Date.now()}`,
  });
  if (error || !data) return { ok: false, error: error?.message ?? 'Activation impossible.' };
  return { ok: true, factorId: data.id, qrCode: data.totp?.qr_code, secret: data.totp?.secret };
}

/** Vérifie un code TOTP (confirme l'activation OU élève la session à aal2). */
export async function verifyTotp(factorId: string, code: string): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/** Désactive un facteur TOTP. */
export async function disableTotp(factorId: string): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}
